import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { api, formatPrice } from "@/lib/api";
import SEO from "@/components/SEO";
import { STORE_NAME } from "@/lib/seo";

export default function Cart() {
    const { items, updateQty, removeItem, totals } = useCart();
    const { settings } = useSettings();
    const cur = settings?.currency_symbol || "Rs.";

    // Server-authoritative shipping. Frontend NEVER decides shipping —
    // we always ask /api/store/shipping/quote so weight/dim/rod logic
    // applies even on the cart page.
    const [quote, setQuote] = useState(null);
    const [quoteLoading, setQuoteLoading] = useState(false);

    const cartKey = items
        .map((it) => `${it.product_id}:${it.variant_id || ""}:${it.quantity}`)
        .join("|");

    useEffect(() => {
        if (items.length === 0) {
            setQuote(null);
            return;
        }
        let cancelled = false;
        setQuoteLoading(true);
        const payload = {
            items: items.map((it) => ({
                product_id: it.product_id,
                variant_id: it.variant_id || null,
                quantity: it.quantity,
            })),
        };
        api.post("/store/shipping/quote", payload)
            .then((r) => { if (!cancelled) setQuote(r.data); })
            .catch(() => { if (!cancelled) setQuote(null); })
            .finally(() => { if (!cancelled) setQuoteLoading(false); });
        return () => { cancelled = true; };
    }, [cartKey]);

    const shipping = Number(quote?.shipping_charge ?? 0);
    const total = Number(quote?.total ?? totals.subtotal);
    const free = Number(settings?.free_shipping_threshold || 0);
    const usedFallback = !!quote?.shipping?.used_fallback_weight;
    const freeApplied = !!quote?.shipping?.free_shipping_applied;

    if (items.length === 0) {
        return (
            <div className="container mx-auto px-4 py-16 text-center" data-testid="cart-empty">
                <SEO title={`Your Cart | ${STORE_NAME}`} description="Your shopping cart at Opensea Fishing Tackles." noindex />
                <ShoppingBag size={48} className="mx-auto text-white/30" />
                <h1 className="font-display text-3xl font-black uppercase mt-4">Your cart is empty</h1>
                <p className="text-white/55 mt-2">Time to load up the tackle box.</p>
                <Link
                    to="/shop"
                    className="inline-block mt-6 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-6 py-3 rounded-sm btn-primary-glow"
                    data-testid="cart-shop-cta"
                >
                    Browse the shop
                </Link>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6 md:py-10" data-testid="cart-page">
            <SEO title={`Your Cart | ${STORE_NAME}`} description="Review your fishing tackle order before checkout." noindex />
            <h1 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tight">Your cart</h1>
            <div className="grid lg:grid-cols-[1fr,360px] gap-6 mt-6 md:mt-8">
                <div className="space-y-3">
                    {items.map((item, idx) => (
                        <div
                            key={`${item.product_id}-${item.variant_id || ""}-${idx}`}
                            className="flex gap-3 p-3 md:p-4 bg-[#121214] border border-[#27272A] rounded-sm"
                            data-testid={`cart-item-${idx}`}
                        >
                            <Link
                                to={`/product/${item.slug}`}
                                className="w-20 h-20 md:w-24 md:h-24 bg-[#0A0A0B] border border-[#27272A] flex-shrink-0 overflow-hidden"
                            >
                                {item.image ? (
                                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                                ) : null}
                            </Link>
                            <div className="flex-1 min-w-0">
                                <Link to={`/product/${item.slug}`} className="block hover:text-[#00BCE5]">
                                    <div className="font-medium text-white truncate">{item.name}</div>
                                </Link>
                                {item.variant_label && (
                                    <div className="text-xs uppercase tracking-widest text-white/55 mt-1 font-display">
                                        {item.variant_label}
                                    </div>
                                )}
                                {item.sku && (
                                    <div className="text-[11px] text-white/40 mt-0.5">SKU: {item.sku}</div>
                                )}
                                <div className="flex items-center justify-between mt-3">
                                    <div className="inline-flex items-center gap-2 border border-[#27272A] rounded-sm px-2 py-1">
                                        <button
                                            onClick={() => updateQty(item.product_id, item.variant_id, item.quantity - 1)}
                                            className="text-white/70 hover:text-white"
                                            aria-label="Decrease"
                                            data-testid={`cart-dec-${idx}`}
                                        >
                                            <Minus size={16} />
                                        </button>
                                        <span className="font-display font-bold w-6 text-center">{item.quantity}</span>
                                        <button
                                            onClick={() => updateQty(item.product_id, item.variant_id, item.quantity + 1)}
                                            className="text-white/70 hover:text-white"
                                            aria-label="Increase"
                                            data-testid={`cart-inc-${idx}`}
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-display font-bold text-white">
                                            {formatPrice(item.price * item.quantity, cur)}
                                        </div>
                                        <button
                                            onClick={() => removeItem(item.product_id, item.variant_id)}
                                            className="text-xs text-white/45 hover:text-[#FF3B30] inline-flex items-center gap-1 mt-1"
                                            data-testid={`cart-remove-${idx}`}
                                        >
                                            <Trash2 size={12} /> Remove
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <aside className="bg-[#121214] border border-[#27272A] rounded-sm p-5 h-fit" data-testid="cart-summary">
                    <h3 className="font-display text-lg uppercase font-black mb-4">Order summary</h3>
                    <FreeShippingHint
                        subtotal={totals.subtotal}
                        threshold={free}
                        enabled={settings?.free_shipping_enabled !== false}
                        applied={freeApplied}
                        appliedTier={quote?.shipping?.shipping_type_applied}
                        quoteReady={!!quote}
                        cur={cur}
                    />
                    <div className="flex justify-between text-sm py-2">
                        <span className="text-white/65">Subtotal</span>
                        <span className="text-white font-medium" data-testid="cart-subtotal">{formatPrice(totals.subtotal, cur)}</span>
                    </div>
                    <div className="flex justify-between text-sm py-2" data-testid="cart-shipping-row">
                        <span className="text-white/65">
                            Shipping {quoteLoading && <span className="text-white/35">…</span>}
                        </span>
                        <span className="text-white font-medium">
                            {freeApplied || shipping === 0 ? "FREE" : formatPrice(shipping, cur)}
                        </span>
                    </div>
                    <p className="text-[10px] text-white/45 mt-2 leading-snug" data-testid="cart-shipping-note">
                        {usedFallback
                            ? "Shipping is a standard estimate (some items missing weight)."
                            : "Shipping calculated from product weight, package size, and your cart contents."}
                    </p>
                    <div className="border-t border-[#27272A] my-3" />
                    <div className="flex justify-between text-base">
                        <span className="font-display uppercase font-black">Total</span>
                        <span className="font-display font-black text-2xl" data-testid="cart-total">{formatPrice(total, cur)}</span>
                    </div>
                    <Link
                        to="/checkout"
                        className="block text-center mt-5 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest py-3.5 rounded-sm hover:bg-[#33C9EA] btn-primary-glow"
                        data-testid="cart-checkout-cta"
                    >
                        Proceed to checkout
                    </Link>
                    <Link
                        to="/shop"
                        className="block text-center mt-3 text-white/65 hover:text-[#00BCE5] text-sm uppercase tracking-widest font-display"
                    >
                        Continue shopping
                    </Link>
                </aside>
            </div>
        </div>
    );
}

function FreeShippingHint({ subtotal, threshold, enabled, applied, appliedTier, quoteReady, cur }) {
    // Stability rules to avoid the "flash then disappear" bug:
    //  1. If admin disabled the feature, never render.
    //  2. If no threshold configured, never render.
    //  3. While the live quote is still in flight, render NOTHING — a brief
    //     hidden state is better than flashing the bar and then yanking it
    //     when the quote returns "rod" / "heavy".
    //  4. After the quote arrives, hide for rod / heavy carts (those tiers
    //     are excluded from free shipping by design).
    if (!enabled || !threshold || threshold <= 0) return null;
    if (!quoteReady) return null;
    if (appliedTier === "rod" || appliedTier === "heavy") return null;

    const reached = applied || subtotal >= threshold;
    const pct = reached ? 100 : Math.max(4, Math.min(100, (subtotal / threshold) * 100));
    const remaining = Math.max(0, threshold - subtotal);

    return (
        <div className="mb-4" data-testid="cart-free-shipping-hint">
            <div className="flex items-center justify-between text-[11px] uppercase tracking-widest font-display">
                <span className={reached ? "text-[#34C759] font-bold" : "text-white/65"}>
                    {reached ? "You unlocked free shipping" : "Free shipping progress"}
                </span>
                {!reached && (
                    <span className="text-[#00BCE5] font-bold" data-testid="cart-free-shipping-remaining">
                        Add {`${cur} ${Math.round(remaining).toLocaleString("en-IN")}`} more
                    </span>
                )}
            </div>
            <div className="mt-1.5 h-1.5 bg-[#0A0A0B] border border-[#27272A] rounded-full overflow-hidden">
                <div
                    className={`h-full transition-all duration-500 ease-out ${reached ? "bg-[#34C759]" : "bg-gradient-to-r from-[#00BCE5] to-[#33C9EA]"}`}
                    style={{ width: `${pct}%` }}
                    data-testid="cart-free-shipping-bar"
                />
            </div>
        </div>
    );
}
